// The VIP win-back email.
//
// Greg's brief, 2026-09-14: "the offer we wanna give to them is to just come back on. It costs
// you nothing. Resume your membership. Pick up where you left off. Get a great fall crab order
// in. It's the best time of year."
//
// ⚠️ THE EMAIL NEVER MENTIONS THE CARD AT ALL, AND THAT IS THE 2026-09-15 REWRITE.
// Greg, reading the first version: "it makes them sound like a deadbeat, like they didn't have
// enough money on their card. Let's just tell them that their membership expired."
//
// He is right twice over. It reads as an accusation, AND it was not even true for a third of the
// people who would receive it: of the two cohorts this offer goes to, 819 had a card decline and
// **421 had auto-renew switched off and were never charged at all** - telling those 421 that
// their payment did not go through describes something that never happened. Of the 819 that did
// decline, 43% are `E00040`, which is OUR OWN lookup failure rather than anything wrong with
// their card, and another 11% are duplicate submissions from our retry logic.
//
// What IS true of every single person on this list, and is the definition of the cohort, is that
// their membership expired on 31 March and was never renewed. So that is what the email says.
// Do not reintroduce a payment sentence here: there is no wording for it that is accurate for
// the whole list.
//
// ⚠️ NO ENGRAVED MALLETS. Greg named them as a "coming soon" item. The engraver is broken, Jess
// asked that we stop selling until it works, and the listing has been off on production since
// 25 August. Promising it here would generate orders for a product we deliberately withdrew.
//
// ⚠️ $79.99, NEVER $99.99. The single price went live 2026-08-17. These members last paid $99.99,
// so quoting the old price would overstate the renewal by $20 and read as a price rise.
//
// ⚠️ NEW PRODUCTS ARE "COMING", NEVER "ADDED", AND CARRY NO DATE. Greg, checking himself
// mid-sentence: "What do we add? Maybe we haven't added much nothing. We're getting ready to add
// some things." Fish is close, caviar is further out. He said "the upcoming month" out loud on
// 2026-09-15 and it is still not in the copy, because a month named in an email to 1,240 people
// is a promise somebody will hold us to.
//
// ⚠️ THE CRAB & CRUISE LINE IS A SHIPPED FEATURE, NOT A TEASER. An active Crab Place VIP gets
// the "Full Package with VIP Upgrade" tier at the plain Full Package price, enforced server-side
// in the C&C checkout by email match. It is safe to state plainly because it works today. Do not
// widen it to "perks all over there" - that is the one perk there is.
//
// ⚠️ IT IS A FREE UPGRADE, NOT A FREE TICKET, AND THE WORDING HAS TO CARRY THAT. The first
// draft read "members ride in the VIP seating ... on us", which a reader can fairly take as the
// whole ticket being free. They still buy the Full Package; what costs nothing is the upgrade.
//
// ⚠️ THE PERK LIST SAYS "up to two tickets an event" AND THAT IS DELIBERATELY THE SMALLER
// NUMBER. The live server allows six a order and is stateless, so it is effectively uncapped; the
// 6 -> 2 per-member-per-event cap was built on 2026-09-04 and its deploy was held for the MTV 80s
// cruise. Promising two is the safe direction while the two numbers disagree - we can honour two
// today whichever way it lands, and a promise of six would break the day the cap ships.
//
// Brand rules apply (customer-facing): no em or en dashes, no AI tells, positive headlines.
// Checked with `npm run brand`.

import { OFFER_TERMS } from './winbackTerms';

const money = (n) => '$' + Number(n).toFixed(2);

const firstNameOf = (full) => {
  const n = String(full || '').trim();
  if (!n || n === '(no name on file)') return 'there';
  return n.split(/\s+/)[0];
};

// The perks, in the order that matters to somebody deciding whether to come back. Money first.
const PERKS = [
  // ⚠️ EVERY THRESHOLD AND EXCLUSION IS STATED, BECAUSE A REP HAS TO HONOUR THIS ON THE PHONE.
  // Greg, 2026-09-15, going through them line by line: the 8% is **seafood SKUs only**, VIP Cash
  // is **orders over $100**, and free packaging needs **$100 of shippable seafood**. The first cut
  // of this list said "on everything you order" and "included on every box", which reads as
  // unconditional and is the kind of promise somebody quotes back to customer service.
  ['8% off market pricing', 'good towards all seafood in the store, all year'],
  ['3% back in VIP Cash', 'on every order over $100, to spend on the next one'],
  // ⚠️ AN EMPTY DETAIL IS A REAL CASE, NOT AN OVERSIGHT. Greg wrote this one as a single bold
  // line, so `perkRows` has to drop the comma rather than render "...services, ".
  ['Free UPS Ground and $20 off UPS Air services', ''],
  // ⚠️ THE $100 IS PACKAGING'S THRESHOLD, AND INSURANCE IS DELIBERATELY QUOTED UNDER IT.
  // Packaging is waived for a VIP at $100 of shippable seafood (`VIP_PACKING_WAIVER_SUBTOTAL_
  // THRESHOLD = 100`, mirrored in the theme and the checkout). Free insurance carries no $100
  // gate of its own, so bundling the two on one line UNDERSTATES insurance. That is the safe
  // direction on a promise: a customer under $100 gets it anyway and is pleasantly surprised,
  // whereas the reverse is a rep arguing about a fee. Split the line if it ever needs to be exact.
  ['Free packaging and insurance', 'on every box over $100'],
  ['Our price match guarantee', 'if you find it cheaper, we match it'],
  ['A $10 coupon every month', 'texted to you'],
  // ⚠️ "Crab &amp; Cruise&reg;" in customer-visible copy, never "Crab and Cruise". Standing
  // brand rule, and this is the one place in the email the name is a product rather than prose.
  // ⚠️ "FREE UPGRADE", NEVER "FREE SEATING". Caught by Rachael on the approval thread,
  // 2026-09-16: "I could see there being people saying we told them they get free C&C tickets."
  // Matt Sr's fix was to put "upgrade" in the bold part, and her own wording supplies the price
  // basis, so a reader cannot come away thinking the ticket itself costs nothing.
  ['Free upgrade to VIP seating at Crab &amp; Cruise&reg;', 'for up to two guests an event, at the Full Package ticket price'],
  ['A free Friday tour', 'of our Crisfield facility, any time you are down'],
];

export function buildWinbackEmail(customer, rep) {
  const first = firstNameOf(customer.name);

  // ⚠️ "EARLIER THIS YEAR" IS TRUE FOR THE COHORT GREG IS MAILING AND FALSE FOR THE REST OF
  // THE LIST. He wrote it on 2026-09-15 with the 31 March 2026 group in front of him, and for
  // those 1,240 people it is exactly right. But this same email is the one offered to the
  // `unknown` cohorts, who lapsed in 2017 through 2025 - telling somebody whose membership ran
  // out in 2019 that it expired earlier this year is plainly wrong, and it is the kind of wrong
  // that makes a reader distrust everything else in the message. So the phrase is his wording
  // when the year matches and names the year when it does not.
  const expiredYear = customer.expired ? Number(String(customer.expired).slice(0, 4)) : null;
  const expiredPhrase = !expiredYear
    ? 'has run out'
    : expiredYear === new Date().getFullYear()
      ? 'expired earlier this year'
      : 'expired back in ' + expiredYear;

  // ⚠️ NO SUPERLATIVES, NO "OFFER", NO "FREE", NO EXCLAMATION MARK IN THE SUBJECT. It went
  // out as "an unbelievable VIP bonus offer for you" for one afternoon and was changed on
  // 2026-09-15 before a single send. Three reasons, and the first is the one that costs money:
  //
  // 1. These go from ashley@ / linda@ / rachael@ - the SAME mailboxes the team uses for order
  //    problems and delivery questions all day. "Unbelievable" is on every spam filter's list,
  //    and the body already carries "free", "no charge" and a dollar figure. Burning those
  //    mailboxes' reputation on a subject line costs us the CS email that has to get through.
  // 2. The call to action IS a reply, so a question sets up the exact thing we want them to do.
  // 3. The whole strategy is that this reads as one person writing to one customer. A superlative
  //    is the one part of the email that reads like a mailshot, and it is read first.
  //
  // ⚠️ "CRAB PLACE" IS IN THE SUBJECT BECAUSE THE SENDER NAME CANNOT CARRY IT. Greg asked for
  // it on 2026-09-15: the mail arrives from "Rachael Nelson", a name a customer who lapsed in
  // March has no reason to recognise. The obvious fix - putting the brand in the From display
  // name - does not survive: X-Unsent makes Outlook compose from the rep's own account, so her
  // Exchange profile decides the display name and the .eml header is ignored. The subject is the
  // only place we control.
  const subject = `${first}, can we get you back in the Crab Place VIP Club?`;

  const perkRows = PERKS.map(([what, detail]) => `
    <tr>
      <td style="padding:5px 0;vertical-align:top;width:18px;color:#DC582A;font-weight:700">&bull;</td>
      <td style="padding:5px 0;font-size:15px;color:#2d3748;line-height:1.5">
        <b style="color:#00395C">${what}</b>${detail ? ', ' + detail : ''}
      </td>
    </tr>`).join('');

  // ⚠️ THE ORDER IS THE ARGUMENT, AND GREG SET IT BY HAND IN THE COMPOSER ON 2026-09-15.
  // The offer box now sits ABOVE the bullets, not below them: greeting, one paragraph of why,
  // then the offer, then the detail that supports it. He could not drag the box in the
  // contenteditable, so he pasted its text where he wanted it and asked for the real box to be
  // moved up and the duplicate removed. This is that layout.
  //
  // ⚠️ IT IS AN OFFER, NEVER A DONE DEAL. The box used to read "Your membership is back on."
  // Greg: "I don't want them to think that we just stuck them back on and now they have work to
  // do." Somebody who believes they have been re-enrolled without asking reads the next line as a
  // charge they have to go and cancel.
  //
  // ⚠️ THE FOUR BULLETS AND THE PERK LIST BELOW THEM OVERLAP ON PURPOSE. The bullets are
  // what is NEW since this person left; the list is everything the membership carries. 8% and the
  // $10 code appear in both because they are both new to somebody who lapsed on 31 March and part
  // of the standing package. Do not dedupe them - the second reading is the one that adds up.
  const html = `
<div style="font-family:Helvetica,Arial,sans-serif;font-size:15px;line-height:1.6;color:#2d3748;max-width:600px">
  <p style="margin:0 0 18px">Hi ${first}, your VIP Club membership ${expiredPhrase}, and we would
  love to have you back.</p>

  <p style="margin:0 0 18px">A lot has happened here since then, and we have big plans going
  forward, especially for our valued VIPs. We've added new perks, polished off the new website for
  easy shopping, new products are coming, and your membership now gets you benefits at our
  Crisfield crab feast events.
  <a href="https://www.crabplace.com/welcome-back/?campaignkw=VIP%20Winback%20Email" style="color:#DC582A;font-weight:700">See everything that's new</a>.</p>

  <p style="background:#fff3ed;border-left:3px solid #DC582A;padding:14px 16px;margin:20px 0">
    <b style="color:#00395C;font-size:17px">We are offering your membership back, at no charge.</b><br>
    Nothing to pay today. You would be covered straight through ${OFFER_TERMS.freeThroughLabel}, which
    is when the club year ends for everybody. After that it renews at ${money(OFFER_TERMS.renewalPrice)}
    on ${OFFER_TERMS.renewsOnLabel}, and you can turn that off whenever you like.
    <br><br>
    <b>All I need is a reply telling me you're in and I'll handle all of the set up for you.</b>
  </p>

  <table cellpadding="0" cellspacing="0" border="0" style="margin:4px 0 22px">
    <tr>
      <td style="padding:5px 0;vertical-align:top;width:18px;color:#DC582A;font-weight:700">&bull;</td>
      <td style="padding:5px 0;font-size:15px;color:#2d3748;line-height:1.5">
        <b style="color:#00395C">We've added new VIP perks.</b> VIPs now get 8% off the regular market
        pricing on all seafood in the store, and a monthly $10 promo code sent via SMS. Factored in
        with the shipping and packaging discounts,
        <b style="color:#DC582A">the average VIP order saves over $72!</b>
      </td>
    </tr>
    <tr>
      <td style="padding:5px 0;vertical-align:top;width:18px;color:#DC582A;font-weight:700">&bull;</td>
      <td style="padding:5px 0;font-size:15px;color:#2d3748;line-height:1.5">
        <b style="color:#00395C">We finished the new website.</b> It works properly on a phone now,
        checkout is quick and easy, and we're adding new features regularly to make for a
        world-class shopping experience.
      </td>
    </tr>
    <tr>
      <td style="padding:5px 0;vertical-align:top;width:18px;color:#DC582A;font-weight:700">&bull;</td>
      <td style="padding:5px 0;font-size:15px;color:#2d3748;line-height:1.5">
        <b style="color:#00395C">New products are coming.</b> Expanded fish and oyster selections
        are next, with caviar behind it, and your membership covers all of it the same way.
      </td>
    </tr>
    <tr>
      <td style="padding:5px 0;vertical-align:top;width:18px;color:#DC582A;font-weight:700">&bull;</td>
      <td style="padding:5px 0;font-size:15px;color:#2d3748;line-height:1.5">
        <b style="color:#00395C">Your membership now reaches Crab &amp; Cruise&reg;.</b> Members get
        the VIP seating upgrade at our crab feast cruises here in Crisfield at no extra cost.
      </td>
    </tr>
  </table>

  <p><b style="color:#00395C">Here's the total list of perks:</b></p>
  <table cellpadding="0" cellspacing="0" border="0" style="margin:6px 0 20px">${perkRows}</table>

  <p style="margin:0 0 26px">Fall is the best crab of the year. They are heavy, they are full, and
  this is the run people wait for. It would be a good one to come back on.</p>

  <p style="margin:0 0 26px"><b>Just hit reply and I will get you set back up.</b> It takes me a
  minute and there is nothing for you to fill in.</p>

  <p style="margin:0 0 6px">Thanks ${first},</p>

  <p style="margin:0">
  <b style="color:#00395C">${rep.name || 'The Crab Place'}</b><br>
  <span style="color:#59707E;font-size:14px">The Crab Place${rep.email ? ' &middot; ' + rep.email : ''}</span></p>
</div>`.trim();

  return { subject, html, to: customer.email || '' };
}
